import { ReactivePaginated } from '@phi/reactive-paginated';
import { listDocuments } from '@phi/frontend/api/calls';
import { searchDocuments } from './procedures/api';
import { Document } from '@phi/onshape';
import { computed, ComputedRef } from 'vue';
import { by } from '@vaaas/fpts/iter';
import { HREF, to_url } from '@phi/newtype';

const next_offset = (x: HREF | null): number => {
    if (!x) return Infinity;
    const url = to_url(x);
    const offset = url.searchParams.get('offset');
    if (offset)
        return parseFloat(offset) || Infinity;
    else
        return Infinity;
};

export class PaginatedOnshapeDocuments extends ReactivePaginated<Document> {
    private existing: ComputedRef<Set<Document['id']>>;
    private reverse: boolean;
    private done_searches: Map<string, number>;
    private listAPI: typeof listDocuments;
    private searchAPI: typeof searchDocuments;

    constructor(list = listDocuments, search = searchDocuments) {
        super([]);
        this.pending = undefined;
        this.existing = computed(() => new Set(this.items.map(x => x.id)));
        this.reverse = true;
        this.done_searches = new Map();
        this.listAPI = list;
        this.searchAPI = search;
    }

    more() {
        if (this.done || this.pending)
            return Promise.resolve(undefined);
        this.fetching = true;
        this.error = undefined;
        return this.pending = this.listAPI(this.items.length)
            .then(x => {
                if (x instanceof Error)
                    this.error = x;
                else {
                    this.add_items(x.items);
                    if (x.next === null)
                        this.done = true;
                }
            }).finally(() => this.cancel());
    }

    search(query: string) {
        if (
            this.done
            || this.pending
            || !query
            || this.done_searches.get(query) === Infinity
        )
            return Promise.resolve(undefined);
        this.fetching = true;
        this.error = undefined;
        return this.pending = this.searchAPI(this.done_searches.get(query) ?? 0, query)
            .then(x => {
                if (x instanceof Error)
                    this.error = x;
                else {
                    this.add_items(x.items);
                    this.done_searches.set(query, next_offset(x.next));
                }
            })
            .finally(() => this.cancel());
    }

    add_items(x: Document[]) {
        const fresh = [
            ...this.items,
            ...x.filter(x => !this.existing.value.has(x.id)),
        ];
        fresh.sort(by(this.sorting_by));
        if (this.reverse) fresh.reverse();
        this.items = fresh;
    }

    private sorting_by(x: Document) {
        return new Date(x.modifiedAt).getTime();
    }

    cancel() {
        this.pending = undefined;
        this.fetching = false;
    }

    has_loaded() {
        return this.items.length > 0
            || this.done === true;
    }
}
